import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTenant } from '../../contexts/TenantContext';
import { useKidsStore } from '../../store/kidsStore';
import { toast } from 'sonner';
import { getBoardSize } from '../../utils/gameLayout';

interface Card {
  id: string;
  url: string;
}

// Configuración de niveles
const LEVELS = {
  neurona: { nombre: 'Neurona', icono: '🧠', pares: 4, imagenes: ['dog', 'cat', 'rabbit', 'fox'] },
  sinapsis: { nombre: 'Sinapsis', icono: '⚡', pares: 5, imagenes: ['dog', 'cat', 'rabbit', 'fox', 'bear'] },
  cerebro: { nombre: 'Cerebro', icono: '🧩', pares: 6, imagenes: ['dog', 'cat', 'rabbit', 'fox', 'bear', 'panda'] },
  genio: { nombre: 'Genio', icono: '💡', pares: 7, imagenes: ['dog', 'cat', 'rabbit', 'fox', 'bear', 'panda', 'tiger'] },
  einstein: { nombre: 'Einstein', icono: '🎓', pares: 8, imagenes: ['dog', 'cat', 'rabbit', 'fox', 'bear', 'panda', 'tiger', 'lion'] }
};

// Mapeo de emojis a Twemoji
const EMOJI_CODES: Record<string, string> = {
  dog: '1f436',
  cat: '1f431',
  rabbit: '1f430',
  fox: '1f98a',
  bear: '1f43b',
  panda: '1f43c',
  tiger: '1f42f',
  lion: '1f981'
};

export default function MemoriaGame() {
  const tenant = useTenant();
  const [searchParams, setSearchParams] = useSearchParams();
  const { selectedKid, registerProgress, registerPlayTime } = useKidsStore();

  const nivel = (searchParams.get('nivel') as keyof typeof LEVELS) || 'neurona';
  const config = LEVELS[nivel] || LEVELS.neurona;

  const [cards, setCards] = useState<Card[]>([]);
  const [flippedIndices, setFlippedIndices] = useState<number[]>([]);
  const [matchedPairs, setMatchedPairs] = useState<number[]>([]);
  const [moves, setMoves] = useState(0);
  const [seconds, setSeconds] = useState(0);
  const [gameStarted, setGameStarted] = useState(false);
  const [showVictory, setShowVictory] = useState(false);
  const [isChecking, setIsChecking] = useState(false);

  useEffect(() => {
    initGame();
  }, [nivel]);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (gameStarted && !showVictory) {
      interval = setInterval(() => {
        setSeconds(s => s + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [gameStarted, showVictory]);

  const initGame = () => {
    const imageData: Card[] = config.imagenes.map(img => ({
      id: img,
      url: `https://cdn.jsdelivr.net/gh/twitter/twemoji@latest/assets/svg/${EMOJI_CODES[img]}.svg`
    }));

    const allCards = [...imageData, ...imageData].sort(() => Math.random() - 0.5);

    setCards(allCards);
    setFlippedIndices([]);
    setMatchedPairs([]);
    setMoves(0);
    setSeconds(0);
    setGameStarted(false);
    setShowVictory(false);
    setIsChecking(false);
  };

  const handleCardClick = (index: number) => {
    if (
      isChecking ||
      flippedIndices.includes(index) ||
      matchedPairs.includes(index) ||
      flippedIndices.length === 2
    ) {
      return;
    }

    if (!gameStarted) {
      setGameStarted(true);
    }

    const newFlipped = [...flippedIndices, index];
    setFlippedIndices(newFlipped);

    if (newFlipped.length === 2) {
      setMoves(m => m + 1);
      setIsChecking(true);
      checkMatch(newFlipped);
    }
  };

  const checkMatch = (indices: number[]) => {
    const [idx1, idx2] = indices;

    if (cards[idx1].id === cards[idx2].id) {
      setTimeout(() => {
        setMatchedPairs(m => [...m, idx1, idx2]);
        setFlippedIndices([]);
        setIsChecking(false);

        if (matchedPairs.length + 2 === cards.length) {
          handleVictory();
        }
      }, 500);
    } else {
      setTimeout(() => {
        setFlippedIndices([]);
        setIsChecking(false);
      }, 1000);
    }
  };

  const handleVictory = async () => {
    setShowVictory(true);
    setGameStarted(false);

    if (!selectedKid) return;

    try {
      await registerProgress(selectedKid.id, {
        juego_id: null,
        tipo_nivel: 'memoria',
        nivel_id: Object.keys(LEVELS).indexOf(nivel) + 1,
        tablero_id: null,
        tiempo: seconds,
        movimientos: moves
      });

      const minutosJugados = Math.ceil(seconds / 60);
      await registerPlayTime(selectedKid.id, minutosJugados);

      toast.success('¡Progreso guardado! 🎉');
    } catch (error) {
      console.error('Error al guardar progreso:', error);
      toast.error('Error al guardar progreso');
    }
  };

  const formatTime = (secs: number) => {
    const mins = Math.floor(secs / 60);
    const seconds = secs % 60;
    return `${String(mins).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  };

  return (
    <div
      className="h-full flex flex-col"
      style={{ background: tenant.theme.colors.gradients.main, fontFamily: tenant.theme.fonts.body.family }}
    >
      {/* Top bar */}
      <div className="bg-black/20 backdrop-blur-sm px-3 py-2 flex items-center gap-2 flex-wrap">
        <span className="text-white font-bold text-base whitespace-nowrap">🎮 Memoria</span>
        <div className="w-px h-5 bg-white/30 hidden sm:block" />
        <div className="flex gap-1 flex-wrap">
          {Object.entries(LEVELS).map(([key, level]) => (
            <button
              key={key}
              onClick={() => setSearchParams({ nivel: key })}
              className={`px-2 py-0.5 rounded text-xs font-semibold transition-all ${
                nivel === key ? 'bg-white' : 'bg-white/20 text-white hover:bg-white/30'
              }`}
              style={nivel === key ? { color: tenant.theme.colors.primary } : {}}
            >
              {level.icono} {level.nombre}
            </button>
          ))}
        </div>
        <div className="flex-1" />
        <span className="text-white text-xs font-semibold">
          {formatTime(seconds)} · {moves} mov · {matchedPairs.length / 2}/{config.pares} ✓
        </span>
        <button
          onClick={initGame}
          title="Nueva Partida"
          className="p-1.5 rounded bg-white/20 hover:bg-white/30 text-white transition-all"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        </button>
      </div>

      {/* Board */}
      <div className="flex-1 flex items-center justify-center p-3">
        <div
          className="grid gap-3 w-full"
          style={{ gridTemplateColumns: 'repeat(4, 1fr)', width: getBoardSize(80), aspectRatio: '1' }}
        >
          {cards.map((card, index) => (
            <div
              key={index}
              onClick={() => handleCardClick(index)}
              className={`
                aspect-square rounded-2xl flex items-center justify-center cursor-pointer
                transition-all shadow-lg
                ${matchedPairs.includes(index) ? 'opacity-70' : 'hover:scale-105'}
              `}
              style={{
                background: flippedIndices.includes(index) || matchedPairs.includes(index)
                  ? matchedPairs.includes(index)
                    ? 'linear-gradient(135deg, #6BCB77, #4D96FF)'
                    : 'linear-gradient(135deg, #FFD93D, #FFA500)'
                  : 'linear-gradient(135deg, #FF6B9D, #C06C84)'
              }}
            >
              {flippedIndices.includes(index) || matchedPairs.includes(index) ? (
                <img src={card.url} alt={card.id} className="w-3/4 h-3/4 object-contain" />
              ) : (
                <span className="text-5xl">❓</span>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Victory Modal */}
      {showVictory && (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl p-10 text-center max-w-md animate-bounce-in">
            <div className="text-8xl mb-4">🎉</div>
            <h2
              className="text-4xl font-bold mb-6"
              style={{ fontFamily: tenant.theme.fonts.primary.family, color: tenant.theme.colors.primary }}
            >
              ¡Felicidades!
            </h2>
            <div className="text-lg text-gray-700 mb-6 space-y-2">
              <p>Completaste el nivel <strong>{config.icono} {config.nombre}</strong></p>
              <p>Tiempo: <strong>{formatTime(seconds)}</strong></p>
              <p>Movimientos: <strong>{moves}</strong></p>
            </div>
            <button
              onClick={initGame}
              className="px-8 py-4 rounded-2xl font-bold text-lg shadow-lg text-white"
              style={{ background: tenant.theme.colors.primary }}
            >
              🎮 Jugar de Nuevo
            </button>
          </div>
        </div>
      )}

      <style>{`
        @keyframes bounce-in {
          0% { transform: scale(0); }
          50% { transform: scale(1.1); }
          100% { transform: scale(1); }
        }
        .animate-bounce-in {
          animation: bounce-in 0.6s;
        }
      `}</style>
    </div>
  );
}
